import { useState } from 'react';

import { ApiError, apiRequest } from '../../api/client.js';
import type { BotCommand, BotSettings, BotVariable } from '../../api/types.js';
import { Alert } from '../../components/Alert.js';
import { EmptyState } from '../../components/EmptyState.js';
import { PlatformIcon } from '../../components/PlatformIcon.js';
import { StatusPill } from '../../components/StatusPill.js';
import { Tooltip } from '../../components/Tooltip.js';
import { useSession } from '../../auth/session-context.js';
import { BotResponseFields, VariableHelp } from './BotResponseFields.js';

/**
 * Custom commands (§102, module brief §39).
 *
 * ==================== THE AUDIENCE PICKER TELLS THE TRUTH ====================
 * There is no `follower` option, because no adapter can establish one — a
 * dropdown entry that configures a command to never fire is the failure §18
 * forbids, wearing a form control.
 *
 * The four levels offered are the four the server accepts, and a role a
 * chosen platform cannot report is refused at SAVE time with the server's own
 * sentence. The Console does not pre-empt that check: the platform/audience
 * matrix lives in `@connaro/bot` and is enforced by the API, and a second copy
 * here would be free to disagree with the one that decides.
 * ============================================================================
 */

const AUDIENCES = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'subscriber', label: 'Subscribers and above' },
  { value: 'moderator', label: 'Moderators and above' },
  { value: 'broadcaster', label: 'Broadcaster only' },
] as const;

type Draft = {
  id: string | null;
  trigger: string;
  aliases: string;
  responses: string[];
  responseMode: 'static' | 'random';
  audience: BotCommand['audience'];
  targetMode: BotCommand['targetMode'];
  targetPlatforms: string[];
  globalCooldownSeconds: number;
  userCooldownSeconds: number;
  enabled: boolean;
};

function draftFrom(command?: BotCommand): Draft {
  return {
    id: command?.id ?? null,
    trigger: command?.trigger ?? '',
    aliases: (command?.aliases ?? []).join(', '),
    responses: command?.responses ?? [''],
    responseMode: command?.responseMode ?? 'static',
    audience: command?.audience ?? 'everyone',
    targetMode: command?.targetMode ?? 'origin',
    targetPlatforms: command?.targetPlatforms ?? [],
    globalCooldownSeconds: command?.globalCooldownSeconds ?? 30,
    userCooldownSeconds: command?.userCooldownSeconds ?? 60,
    enabled: command?.enabled ?? true,
  };
}

export function BotCommands({
  commands,
  variables,
  settings,
  onChanged,
}: {
  commands: BotCommand[];
  variables: BotVariable[];
  settings: BotSettings;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const { can } = useSession();
  const mayManage = can('bot.commands.manage');

  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    if (draft === null) return;
    setBusy(true);
    setError(null);

    try {
      const body = {
        trigger: draft.trigger,
        aliases: draft.aliases
          .split(',')
          .map((alias) => alias.trim())
          .filter((alias) => alias !== ''),
        responses: draft.responses.filter((response) => response.trim() !== ''),
        responseMode: draft.responseMode,
        audience: draft.audience,
        targetMode: draft.targetMode,
        targetPlatforms: draft.targetPlatforms,
        globalCooldownSeconds: draft.globalCooldownSeconds,
        userCooldownSeconds: draft.userCooldownSeconds,
        enabled: draft.enabled,
      };

      await apiRequest(draft.id === null ? '/bot/commands' : `/bot/commands/${draft.id}`, {
        method: draft.id === null ? 'POST' : 'PUT',
        body,
      });

      setDraft(null);
      await onChanged();
    } catch (caught) {
      /*
       * The SERVER's sentence, rendered as given. It is the one that names the
       * colliding alias, the unknown variable or the platform that cannot
       * report the role — all things this page has no way to work out.
       */
      setError(caught instanceof ApiError ? caught.message : 'That command could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/bot/commands/${id}`, { method: 'DELETE' });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That command could not be deleted.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bot__section">
      {error !== null && <Alert tone="error">{error}</Alert>}

      {mayManage && draft === null && (
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setDraft(draftFrom());
          }}
        >
          New command
        </button>
      )}

      {draft !== null && (
        <form
          className="bot__form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="bot__row">
            <div className="composer__field">
              <label className="field__label" htmlFor="command-trigger">
                Command
              </label>
              <input
                id="command-trigger"
                className="field__input"
                value={draft.trigger}
                placeholder={`${settings.commandPrefix}socials`}
                onChange={(event) => {
                  setDraft({ ...draft, trigger: event.target.value });
                }}
              />
            </div>

            <div className="composer__field">
              <label className="field__label" htmlFor="command-aliases">
                Aliases
              </label>
              <input
                id="command-aliases"
                className="field__input"
                value={draft.aliases}
                placeholder="links, social"
                onChange={(event) => {
                  setDraft({ ...draft, aliases: event.target.value });
                }}
              />
              <span className="field__hint">
                Comma separated. Aliases and command names share one list, so an alias cannot
                reuse another command’s name.
              </span>
            </div>
          </div>

          <BotResponseFields
            responses={draft.responses}
            mode={draft.responseMode}
            variables={variables}
            onChange={(responses, mode) => {
              setDraft({ ...draft, responses, responseMode: mode });
            }}
          />

          <div className="bot__row">
            <div className="composer__field">
              <label className="field__label" htmlFor="command-audience">
                Who can use it
              </label>
              <select
                id="command-audience"
                className="field__input"
                value={draft.audience}
                onChange={(event) => {
                  setDraft({ ...draft, audience: event.target.value as Draft['audience'] });
                }}
              >
                {AUDIENCES.map((audience) => (
                  <option key={audience.value} value={audience.value}>
                    {audience.label}
                  </option>
                ))}
              </select>
              {/*
               * Said here rather than discovered at 2am. Not every platform
               * reports roles, and the API refuses the combination at save
               * time with the platform named.
               */}
              <span className="field__hint">
                Not every platform tells Connaro who is a subscriber or moderator. Saving will say
                so if one of your platforms cannot.
              </span>
            </div>

            <div className="composer__field">
              <label className="field__label" htmlFor="command-global-cooldown">
                Cooldown (seconds)
              </label>
              <input
                id="command-global-cooldown"
                className="field__input"
                type="number"
                min={0}
                value={draft.globalCooldownSeconds}
                onChange={(event) => {
                  setDraft({ ...draft, globalCooldownSeconds: Number(event.target.value) });
                }}
              />
            </div>

            <div className="composer__field">
              <label className="field__label" htmlFor="command-user-cooldown">
                Per viewer (seconds)
              </label>
              <input
                id="command-user-cooldown"
                className="field__input"
                type="number"
                min={0}
                value={draft.userCooldownSeconds}
                onChange={(event) => {
                  setDraft({ ...draft, userCooldownSeconds: Number(event.target.value) });
                }}
              />
            </div>
          </div>

          <label className="bot__switch">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => {
                setDraft({ ...draft, enabled: event.target.checked });
              }}
            />
            <span>Enabled</span>
          </label>

          <div className="bot__actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save command'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {commands.length === 0 ? (
        <EmptyState title="No commands yet">A command is a word a viewer types that the bot answers. Try one that answers with your links.</EmptyState>
      ) : (
        <ul className="bot__list">
          {commands.map((command) => (
            <li className="bot__item" key={command.id}>
              <div className="bot__item-head">
                <span className="bot__item-name">
                  {settings.commandPrefix}
                  {command.trigger ?? '(no name)'}
                </span>
                <StatusPill
                  tone={command.enabled ? 'ok' : 'idle'}
                  label={command.enabled ? 'Enabled' : 'Disabled'}
                />
                {/*
                 * WHERE IT GOES, as marks rather than a sentence. `origin`
                 * shows nothing because the answer depends on the message —
                 * inventing a platform list for it would be a claim the
                 * command does not make.
                 */}
                {command.targetMode === 'selected' &&
                  command.targetPlatforms.map((platform) => (
                    <PlatformIcon
                      key={platform}
                      platform={platform}
                      displayName={platform}
                      size={16}
                      label={platform}
                    />
                  ))}
              </div>

              {command.aliases.length > 0 && (
                <span className="field__hint">
                  Also{' '}
                  {command.aliases
                    .map((alias) => `${settings.commandPrefix}${alias}`)
                    .join(', ')}
                </span>
              )}

              <span className="bot__item-detail">{command.responses[0] ?? ''}</span>

              <span className="field__hint">
                {command.audience === 'everyone' ? 'Everyone' : `${command.audience}s and above`} ·
                used {String(command.useCount)} time{command.useCount === 1 ? '' : 's'}
                {command.targetMode === 'origin' ? ' · replies where it was asked' : ''}
              </span>

              <span className="field__hint bot__cooldowns">
                <Tooltip tip="After the command runs, nobody can use it again until this many seconds have passed.">
                  <span>Cooldown {String(command.globalCooldownSeconds)}s</span>
                </Tooltip>
                <Tooltip tip="After one viewer uses the command, that viewer must wait this long. Other viewers are not affected.">
                  <span>Per viewer {String(command.userCooldownSeconds)}s</span>
                </Tooltip>
              </span>

              {mayManage && (
                <div className="bot__actions">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setDraft(draftFrom(command));
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() => void remove(command.id)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <VariableHelp variables={variables} />
    </div>
  );
}
